/**
 * OAuthButtons — social sign-in buttons ("Continue with Google / GitHub").
 *
 * Pure-ish view: data comes from the `useOAuthProviders` probe and the side
 * effect (opening the OAuth flow) is delegated to the `useOAuthSignIn`
 * controller hook. signIn() prefers a popup window — nicer on the website
 * because it keeps the user on our page — and automatically falls back to the
 * current full-page redirect when popups are blocked (e.g. inside the UE
 * plugin's WebView2). One code path, both contexts.
 *
 * Renders a button ONLY for providers the probe reports enabled. If neither
 * provider is enabled (or the probe is still loading / failed closed), the
 * component renders nothing at all — no empty "or" divider, no dead controls.
 * (The buttons are never hidden for OAuth-only users: they have no password,
 * so they need the button to sign in inside the plugin too.)
 *
 * Drop it under the email/password form on LoginPage / RegisterPage with
 * `showDivider` to get the "or" separator above the buttons.
 *
 * Icons are lucide-react only (project rule): `Chrome` stands in for Google
 * (lucide has no Google brand mark) and `Github` for GitHub. Buttons reuse
 * the shared `Button` secondary variant so they match the auth-card chrome.
 */

import { Chrome, Github } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useOAuthProviders, useOAuthSignIn } from '@/lib/auth'
import type { OAuthProviderId } from '@/lib/auth'
import { Button } from '@/shared/components/forms/Button'

interface ProviderConfig {
  id: OAuthProviderId
  label: string
  Icon: LucideIcon
}

const PROVIDERS: ProviderConfig[] = [
  { id: 'google', label: 'Continue with Google', Icon: Chrome },
  { id: 'github', label: 'Continue with GitHub', Icon: Github },
]

export interface OAuthButtonsProps {
  /**
   * When true, render an "or" divider above the buttons (for placement under
   * an email/password form). Only shown when at least one provider is enabled.
   */
  showDivider?: boolean
  /**
   * Icon-only row for tight surfaces (e.g. the CC header strip).
   */
  compact?: boolean
}

export function OAuthButtons({ showDivider = false, compact = false }: OAuthButtonsProps) {
  const { providers, isLoading } = useOAuthProviders()
  const { signIn } = useOAuthSignIn()

  // Fail closed: while probing, or when no provider is enabled, render
  // nothing — never show an empty divider or a dead button.
  if (isLoading) return null

  const enabled = PROVIDERS.filter((p) => providers[p.id])
  if (enabled.length === 0) return null

  if (compact) {
    return (
      <div className="flex items-center gap-1.5">
        {enabled.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => signIn(id)}
            title={label}
            aria-label={label}
            className="flex size-7 items-center justify-center rounded border border-ue-border-medium bg-ue-bg-darkest/80 text-ue-text-secondary hover:text-ue-text-primary hover:border-ue-accent-primary transition-colors"
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {showDivider && (
        <div className="flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-ue-border-medium/60" />
          <span className="text-[11px] uppercase tracking-wide text-ue-text-tertiary">
            or
          </span>
          <span className="h-px flex-1 bg-ue-border-medium/60" />
        </div>
      )}

      <div className="space-y-2">
        {enabled.map(({ id, label, Icon }) => (
          <Button
            key={id}
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() => signIn(id)}
          >
            <Icon className="w-4 h-4 mr-2 shrink-0" />
            {label}
          </Button>
        ))}
      </div>
    </div>
  )
}
